import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import {
  ArrowLeft,
  Calculator,
  ReceiptText,
  Calendar,
  FileText,
  Edit,
  Send,
} from "lucide-react";
import DashboardContainer from "../layout/DashboardContainer";
import {
  Invoice,
  InvoiceStatus,
  InvoiceStatusLabels,
  InvoiceStatusColors,
} from "@/types/invoice";
import InvoiceAPIService from "@/services/invoice-api";
import { Currency, LocalDate } from "@/components/i18n";
import { useCountry } from "@/contexts/CountryContext";

const LineItemDisplay: React.FC<{
  item: Invoice["lineItems"][0];
  index: number;
}> = ({ item, index }) => {
  const calculateSubtotal = () => {
    return item.rate * item.quantity;
  };

  return (
    <Card className="p-4">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">Line Item {index + 1}</h4>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              Product/Service
            </label>
            <div className="p-3 bg-muted rounded-md text-sm">
              {item.product}
            </div>
          </div>

          {item.description && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                Description
              </label>
              <div className="p-3 bg-muted rounded-md text-sm">
                {item.description}
              </div>
            </div>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              Rate
            </label>
            <div className="p-3 bg-muted rounded-md text-sm">
              <Currency amount={parseFloat(item.rate.toString()) || 0} />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              Quantity / Hours
            </label>
            <div className="p-3 bg-muted rounded-md text-sm">
              {item.quantity}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              Tax
            </label>
            <div className="p-3 bg-muted rounded-md text-sm">
              <Currency amount={parseFloat(item.tax.toString()) || 0} />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              Total
            </label>
            <div className="p-3 bg-muted rounded-md text-sm font-medium">
              <Currency amount={parseFloat(item.total.toString()) || 0} />
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export function ViewInvoiceDetails({
  invoice,
  onBack,
  onEdit,
}: {
  invoice: Invoice;
  onBack: () => void;
  onEdit?: (invoice: Invoice) => void;
}) {
  const { toast } = useToast();
  const { formatCurrency, formatDate, currentCountry } = useCountry();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmitInvoice = async () => {
    try {
      setIsSubmitting(true);
      await InvoiceAPIService.submitInvoice(invoice.id);

      toast({
        title: "Success",
        description: "Invoice submitted successfully",
      });

      // Refresh the page or navigate back to trigger a refresh
      onBack();
    } catch (error) {
      console.error("Error submitting invoice:", error);
      toast({
        title: "Error",
        description: "Failed to submit invoice. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadgeClass = (status: InvoiceStatus) => {
    return InvoiceStatusColors[status];
  };

  const headerContent = (
    <div className="flex flex-col sm:flex-row gap-2">
      <Button variant="outline" onClick={onBack} className="w-full sm:w-auto">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Invoices
      </Button>
      {invoice.status === "DRAFT" && (
        <div className="flex gap-2">
          {onEdit && (
            <Button
              onClick={() => onEdit(invoice)}
              className="w-full sm:w-auto"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Invoice
            </Button>
          )}
          <Button
            onClick={handleSubmitInvoice}
            disabled={isSubmitting}
            className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
            data-testid="submit-invoice-button"
          >
            <Send className="h-4 w-4 mr-2" />
            {isSubmitting ? "Submitting..." : "Submit Invoice"}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <DashboardContainer
      title={`Invoice ${invoice.invoiceNumber || `#${invoice.id}`}`}
      headerContent={headerContent}
    >
      <div className="space-y-6">
        {/* Invoice Status & Overview */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <ReceiptText className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl">
                    {invoice.invoiceNumber || `Invoice #${invoice.id}`}
                  </CardTitle>
                  <p className="text-muted-foreground">
                    Created on <LocalDate date={invoice.createdAt} format="medium" />
                  </p>
                </div>
              </div>
              <Badge className={getStatusBadgeClass(invoice.status)}>
                {InvoiceStatusLabels[invoice.status]}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Due Date</p>
                  <p className="text-sm text-muted-foreground">
                    <LocalDate date={invoice.dueDate} format="medium" />
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Line Items</p>
                  <p className="text-sm text-muted-foreground">
                    {invoice.lineItems.length} item
                    {invoice.lineItems.length !== 1 ? "s" : ""}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calculator className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Total Amount</p>
                  <p className="text-sm text-muted-foreground">
                    <Currency 
                      amount={parseFloat(invoice.grandTotal.toString()) || 0}
                      currencyCode={currentCountry?.currencyCode}
                    />
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invoice Details */}
        <Card>
          <CardHeader>
            <CardTitle>Invoice Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  Invoice Number
                </label>
                <div className="p-3 bg-muted rounded-md text-sm">
                  {invoice.invoiceNumber || "Auto-generated"}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  Due Date
                </label>
                <div className="p-3 bg-muted rounded-md text-sm">
                  <LocalDate date={invoice.dueDate} format="medium" />
                </div>
              </div>
            </div>

            {invoice.notes && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  Notes
                </label>
                <div className="p-3 bg-muted rounded-md text-sm">
                  {invoice.notes}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Line Items */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Line Items</h3>

          <div className="space-y-4">
            {invoice.lineItems.map((item, index) => (
              <LineItemDisplay key={item.id} item={item} index={index} />
            ))}
          </div>
        </div>

        {/* Invoice Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Invoice Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal:</span>
                <span>
                  <Currency 
                    amount={parseFloat(invoice.subtotal.toString()) || 0}
                    currencyCode={currentCountry?.currencyCode}
                  />
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Tax:</span>
                <span>
                  <Currency 
                    amount={parseFloat(invoice.totalTax.toString()) || 0}
                    currencyCode={currentCountry?.currencyCode}
                  />
                </span>
              </div>
              <div className="border-t pt-3">
                <div className="flex justify-between text-lg font-medium">
                  <span>Total:</span>
                  <span>
                    <Currency 
                      amount={parseFloat(invoice.grandTotal.toString()) || 0}
                      currencyCode={currentCountry?.currencyCode}
                    />
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardContainer>
  );
}
